'use client';

import { Card, Skeleton } from '@repo/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import type { DegreesOfFreedomResult } from '../../../lib/stats/degrees-of-freedom';

interface DegreesOfFreedomProps {
  visualizationData: DegreesOfFreedomResult | null;
}

export function DegreesOfFreedom({ visualizationData: result }: DegreesOfFreedomProps) {
  if (!result) {
    return (
      <ScrollableContainer>
        <div className="space-y-6">
          <Card className="p-4">
            <Skeleton className="h-32 w-full" />
          </Card>
        </div>
      </ScrollableContainer>
    );
  }

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Degrees of Freedom Result</h3>
          <div className="space-y-4">
            <div className="flex flex-col space-y-2">
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-medium">Degrees of Freedom:</span>
                <span className="text-xl font-bold">{result.degreesOfFreedom}</span>
              </div>
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-medium">Formula Used:</span>
                <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{result.formula}</span>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Explanation:</h4>
              <p className="text-gray-700 dark:text-gray-300">{result.explanation}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">How to Use This Result</h3>
          <div className="space-y-2 text-gray-700 dark:text-gray-300">
            <p>The degrees of freedom value is used in various statistical tests:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Look up critical values in statistical tables</li>
              <li>Determine the probability associated with test statistics</li>
              <li>Calculate confidence intervals</li>
              <li>Evaluate the significance of test results</li>
            </ul>
            <p className="mt-2">For more information, refer to the related calculators such as t-tests, ANOVA, or chi-square tests.</p>
          </div>
        </Card>
      </div>
    </ScrollableContainer>
  );
} 